<div class="max-w-md mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
  <!-- Error message when no invite found -->
  @if (errorMessage()) {
    <div class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
      <p>{{ errorMessage() }}</p>
    </div>
  }

  <!-- Pending invite - Needs acceptance -->
  @if (inviteExists() && !inviteAccepted() && !existingUser()) {
    <div class="text-center">
      <h2 class="text-xl font-bold text-gray-800 dark:text-white mb-4">You've been invited!</h2>
      <p class="text-gray-600 dark:text-gray-300 mb-6">You've been invited to join as a {{ inviteRole() }}.</p>
      <button 
        (click)="acceptInvite()" 
        class="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 focus:ring-offset-blue-200 text-white transition ease-in duration-200 text-base font-semibold shadow-md rounded-lg">
        Accept Invitation
      </button>
    </div>
  }

  @if (existingUser() || registrationComplete()) {
    <div class="text-center">
      <h2 class="text-xl font-bold text-gray-800 dark:text-white mb-4">You've accepted the invite!</h2>
      <p class="text-gray-600 dark:text-gray-300 mb-6">You've been invited to join as a {{ inviteRole() }}.</p>    
      <button 
        (click)="signIn()" 
        class="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 focus:ring-offset-blue-200 text-white transition ease-in duration-200 text-base font-semibold shadow-md rounded-lg">
        Sign In
      </button>
    </div>
  }


  <!-- Registration form after accepting invite -->
  @if (inviteAccepted() && !registrationComplete()) {
    <form [formGroup]="registrationForm" (ngSubmit)="onSubmit()" class="space-y-4 animate-fadeIn">
      <h2 class="text-xl font-bold text-gray-800 dark:text-white mb-4">Create Your Account</h2>
      
      <!-- Display Name -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">Display Name</label>
        <input type="text" formControlName="displayName" 
               class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        @if (displayName.invalid && (displayName.dirty || displayName.touched)) {
          <p class="mt-1 text-sm text-red-600">Display name is required</p>
        }
      </div>
      
      <!-- First Name - Added field -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">First Name</label>
        <input type="text" formControlName="firstName" 
               class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        @if (firstName.invalid && (firstName.dirty || firstName.touched)) {
          <p class="mt-1 text-sm text-red-600">First name is required</p>
        }
      </div>
      
      <!-- Last Name - Added field -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">Last Name</label>
        <input type="text" formControlName="lastName" 
               class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        @if (lastName.invalid && (lastName.dirty || lastName.touched)) {
          <p class="mt-1 text-sm text-red-600">Last name is required</p>
        }
      </div>
      
      <!-- Profile Photo -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">Profile Photo</label>
        <div class="mt-1 flex items-center">
          <span class="inline-block h-12 w-12 rounded-full overflow-hidden bg-gray-100">
            @if (photoPreview) {
              <img [src]="photoPreview" alt="Profile preview" class="h-full w-full object-cover" />
            } @else {
              <svg class="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14.75c2.34 0 4.25-1.91 4.25-4.25S14.34 6.25 12 6.25 7.75 8.16 7.75 10.5 9.66 14.75 12 14.75zm0 1.5C9.26 16.25 7 14.14 7 11.5S9.26 6.75 12 6.75s5 2.11 5 4.75-2.26 4.75-5 4.75z"></path>
                <path d="M22.25 12c0-5.66-4.59-10.25-10.25-10.25S1.75 6.34 1.75 12 6.34 22.25 12 22.25 22.25 17.66 22.25 12zM12 20.75c-4.83 0-8.75-3.92-8.75-8.75S7.17 3.25 12 3.25s8.75 3.92 8.75 8.75-3.92 8.75-8.75 8.75z"></path>
              </svg>
            }
          </span>
          <button type="button" 
                  (click)="fileInput.click()" 
                  class="ml-5 bg-white dark:bg-gray-600 py-2 px-3 border border-gray-300 dark:border-gray-500 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none">
            Change
          </button>
          <input #fileInput type="file" 
                 (change)="onFileSelected($event)" 
                 accept="image/*" 
                 class="hidden" />
        </div>
      </div>
      
      <!-- Password -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">Password</label>
        <input type="password" formControlName="password" 
               class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        @if (password.invalid && (password.dirty || password.touched)) {
          <div class="mt-1 text-sm text-red-600">
            @if (password.errors?.['required']) {
              <p>Password is required</p>
            }
            @if (password.errors?.['minlength']) {
              <p>Password must be at least 8 characters</p>
            }
          </div>
        }
      </div>
      
      <!-- Confirm Password -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">Confirm Password</label>
        <input type="password" formControlName="confirmPassword" 
               class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        @if (confirmPassword.invalid && (confirmPassword.dirty || confirmPassword.touched)) {
          <div class="mt-1 text-sm text-red-600">
            @if (confirmPassword.errors?.['required']) {
              <p>Please confirm your password</p>
            }
          </div>
        }
        @if (registrationForm.errors?.['passwordMismatch'] && confirmPassword.dirty) {
          <p class="mt-1 text-sm text-red-600">Passwords do not match</p>
        }
      </div>
      
      <!-- About Me -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">About Me</label>
        <textarea formControlName="aboutMe" rows="3" 
                  class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"></textarea>
      </div>
      
      <div class="flex space-x-4">
        <button type="submit" 
                [disabled]="registrationForm.invalid" 
                class="flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 focus:ring-offset-blue-200 text-white transition ease-in duration-200 text-base font-semibold shadow-md rounded-lg disabled:opacity-50">
          Create Account
        </button>
        <button type="button" 
                (click)="cancelRegistration()" 
                class="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 focus:ring-gray-500 focus:ring-offset-gray-200 text-gray-700 transition ease-in duration-200 text-base font-semibold shadow-md rounded-lg">
          Cancel
        </button>
      </div>
    </form>
  }

  <!-- Cancel message -->
  @if (showCancelMessage()) {
    <div class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 my-4" role="alert">
      <p>Your invitation is still pending. You can accept it later.</p>
    </div>
  }
</div>
